// Verbinding met de room-websocket in de backend. Nog geen spellogica, alleen
// wie er in de room zit en een echo zodat je kunt zien dat twee clients
// elkaar echt bereiken.

import type { GameState } from './game'
import { log, describeError } from './log'
import { wsUrl } from './net'

export interface RoomMember {
  user_id: string
  name: string
  /** Avatar-hash van Discord, of null. De client bouwt er zelf de proxy-URL bij. */
  avatar_hash: string | null
}

export interface RoomState {
  instance_id: string
  members: RoomMember[]
}

/** Alles wat de server naar ons stuurt. */
export type ServerMessage =
  | { op: 'welcome'; you: RoomMember; server_time: number }
  | { op: 'state'; room: RoomState; game: GameState }
  | { op: 'pong'; server_time: number }
  | { op: 'error'; message: string }

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface RoomHandlers {
  onStatus(status: ConnectionStatus): void
  onMessage(message: ServerMessage): void
}

interface RoomIdentity {
  instanceId: string
  accessToken?: string
  channelId?: string | null
  guildId?: string | null
  devName?: string
}

const MAX_BACKOFF_MS = 15_000
const HEARTBEAT_MS = 25_000

export class RoomConnection {
  private socket: WebSocket | null = null
  private attempt = 0
  private heartbeat: ReturnType<typeof setInterval> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private closedByUs = false

  constructor(
    private readonly identity: RoomIdentity,
    private readonly handlers: RoomHandlers,
  ) {}

  connect(): void {
    this.closedByUs = false
    this.open()
  }

  close(): void {
    this.closedByUs = true
    this.stopHeartbeat()
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.socket?.close(1000, 'client sluit af')
    this.socket = null
    this.handlers.onStatus('closed')
  }

  send(payload: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      log('warn', 'Bericht niet verstuurd, websocket is niet open')
      return
    }
    this.socket.send(JSON.stringify(payload))
  }

  private open(): void {
    const url = wsUrl('/ws', { instance_id: this.identity.instanceId })
    this.handlers.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting')
    log('info', `Websocket verbinden met ${url}`)

    const socket = new WebSocket(url)
    this.socket = socket

    socket.onopen = () => {
      this.attempt = 0
      log('ok', 'Websocket open, hello sturen')
      // De server weet nog niet wie we zijn. Dat vertellen we in het eerste
      // bericht, met het access token zodat de server het bij Discord kan
      // verifieren in plaats van ons op ons woord te geloven.
      socket.send(
        JSON.stringify({
          op: 'hello',
          access_token: this.identity.accessToken,
          dev_name: this.identity.devName,
          channel_id: this.identity.channelId ?? null,
          guild_id: this.identity.guildId ?? null,
        }),
      )
      this.startHeartbeat()
      this.handlers.onStatus('open')
    }

    socket.onmessage = (event) => {
      let message: ServerMessage
      try {
        message = JSON.parse(String(event.data)) as ServerMessage
      } catch (error) {
        log('warn', `Onleesbaar bericht van de server: ${describeError(error)}`)
        return
      }
      if (message.op === 'error') {
        log('error', `Server: ${message.message}`)
      }
      this.handlers.onMessage(message)
    }

    socket.onerror = () => {
      log('warn', 'Websocket-fout')
    }

    socket.onclose = (event) => {
      this.stopHeartbeat()
      this.socket = null
      if (this.closedByUs) return

      log('warn', `Websocket dicht (code ${event.code}${event.reason ? `, ${event.reason}` : ''})`)
      this.scheduleRetry()
    }
  }

  private scheduleRetry(): void {
    this.attempt += 1
    const delay = Math.min(1000 * 2 ** (this.attempt - 1), MAX_BACKOFF_MS)
    this.handlers.onStatus('reconnecting')
    log('info', `Opnieuw verbinden over ${Math.round(delay / 1000)}s (poging ${this.attempt})`)
    this.retryTimer = setTimeout(() => this.open(), delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeat = setInterval(() => this.send({ op: 'ping' }), HEARTBEAT_MS)
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat)
      this.heartbeat = null
    }
  }
}
